import React, { useState } from 'react';
import { QrCode, Check, MapPin, Eye, Sparkles, ArrowRight, Compass, ChevronRight, Trophy, Star, Filter, Lock, Gift } from 'lucide-react';
import { Artifact } from '../types';

const GUIDE_STEPS = [
  {
    step: '01',
    phase: 'BƯỚC 1 · LÊN TỌA ĐỘ 🗺️',
    title: 'Check-in Tầng 1 & Tầng 2',
    desc: 'Mở bản đồ hoặc xem gợi ý nhiệm vụ trên web, sau đó di chuyển đến không gian trưng bày (Tầng 1 hoặc Tầng 2 VNFAM).',
    badge: 'Tầng 1 & Tầng 2 VNFAM',
    icon: MapPin,
    iconColor: 'text-[#ffd166]',
    iconBg: 'bg-[#ffb703]/20 border-[#ffd166]/50',
  },
  {
    step: '02',
    phase: 'BƯỚC 2 · SOI BẢO VẬT 👁️',
    title: 'Đối chiếu Bảo vật Quốc gia',
    desc: 'Quan sát tác phẩm thực tế, kiểm tra tên tác giả và số hiệu trên biển chú giải để đảm bảo bạn đứng đúng hiện vật độc quyền.',
    badge: 'Biển chú giải di sản',
    icon: Eye,
    iconColor: 'text-[#06d6a0]',
    iconBg: 'bg-[#06d6a0]/20 border-[#06d6a0]/50',
  },
  {
    step: '03',
    phase: 'BƯỚC 3 · QUÉT QR 📸',
    title: 'Mở camera & Quét mã thần tốc',
    desc: 'Bật máy ảnh trực tiếp trên trình duyệt (không cần cài app) và hướng vào mã QR dán tại chân đế hiện vật để mở khóa.',
    badge: 'Mã QR tại chân hiện vật',
    icon: QrCode,
    iconColor: 'text-[#ff3366]',
    iconBg: 'bg-[#ff3366]/20 border-[#ff3366]/50',
    hasScannerCta: true,
  },
  {
    step: '04',
    phase: 'BƯỚC 4 · LỤM TEM 💎',
    title: 'Nhận tem số & Tích lũy XP',
    desc: 'Tem số được đóng dấu tức thì vào Balo Thợ Săn, cộng ngay +50 XP vào Hồ Sơ và mở ra manh mối cho điểm dừng tiếp theo!',
    badge: '+1 Tem Hiếm & +50 XP',
    icon: Sparkles,
    iconColor: 'text-[#c084fc]',
    iconBg: 'bg-[#8b5cf6]/20 border-[#c084fc]/50',
  },
];

interface CollectionViewProps {
  artifacts: Artifact[];
  onOpenArtifact: (artifact: Artifact) => void;
  onOpenScanner: () => void;
}

export const CollectionView: React.FC<CollectionViewProps> = ({
  artifacts,
  onOpenArtifact,
  onOpenScanner,
}) => {
  const [activeFilter, setActiveFilter] = useState<'all' | 'unlocked' | 'locked'>('all');

  const collectedCount = artifacts.filter((a) => a.unlocked).length;
  const totalCount = artifacts.length;
  const percent = Math.round((collectedCount / totalCount) * 100);

  // Next locked artifact for mission
  const nextTarget = artifacts.find((a) => !a.unlocked) || artifacts[0];

  const filteredArtifacts = artifacts.filter((art) => {
    if (activeFilter === 'unlocked') return art.unlocked;
    if (activeFilter === 'locked') return !art.unlocked;
    return true;
  });

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-10 animate-fade-in pb-24">
      {/* COLLECTION HERO: GAMER BACKPACK BINDER */}
      <section className="relative min-h-[380px] sm:min-h-[440px] rounded-3xl overflow-hidden border-2 border-[#ffb703]/50 shadow-2xl flex items-center p-6 sm:p-12 text-[#fff8ee] bg-[#23112a]">
        <div className="absolute inset-0 z-0">
          <img
            src="/images/museum/web/interior-gallery-lacquer-and-sculpture.webp"
            alt="Bộ sưu tập Quốc bảo"
            className="w-full h-full object-cover filter brightness-75 saturate-125"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#170c1c] via-[#24122d]/92 to-[#190c1f]/75" />
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#ff3366]/20 blur-3xl pointer-events-none" />
        </div>

        <div className="relative z-10 max-w-xl space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#ffb703]/20 border border-[#ffd166]/50 text-[#ffd166] text-xs font-black uppercase tracking-wider shadow-sm">
            <Sparkles className="w-3.5 h-3.5" />
            <span>BỘ SƯU TẬP QUỐC BẢO HUYỀN THOẠI</span>
          </div>

          <h1 className="text-3xl sm:text-5xl font-black text-white leading-tight tracking-tight">
            Balo Thợ Săn <br />
            <span className="text-[#ffd166]">Bảo Vật Vô Giá</span> 🎒
          </h1>

          <p className="text-sm sm:text-base text-[#fcefe1] leading-relaxed">
            Khám phá trực tiếp tại Bảo tàng và săn trọn 09 con tem số hiếm bằng cách quét mã QR dán tại chân hiện vật. Thu thập đủ bộ để nhận Huy Hiệu Vàng và mở khóa toàn bộ câu chuyện bí ẩn!
          </p>

          {/* Gamified Stats Progress */}
          <div className="space-y-2 pt-2">
            <div className="flex items-center justify-between text-xs font-bold">
              <span className="text-[#ffd166] flex items-center gap-1">
                <Star className="w-3.5 h-3.5 fill-current text-[#ffd166]" />
                Tiến trình mở khóa: {collectedCount}/{totalCount} Tem
              </span>
              <span className="text-[#06d6a0] font-black">{percent}% Hoàn thành</span>
            </div>

            <div className="w-full h-3.5 bg-black/50 rounded-full overflow-hidden border border-[#ffd166]/40 p-0.5 shadow-inner">
              <div
                className="h-full bg-gradient-to-r from-[#ff3366] via-[#ffb703] to-[#06d6a0] rounded-full transition-all duration-700 shadow-md"
                style={{ width: `${percent}%` }}
              />
            </div>
          </div>

          {/* Stats Badges */}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <span className="px-3 py-1.5 rounded-xl bg-[#06d6a0]/20 text-[#a7f3d0] text-xs font-black border border-[#06d6a0]/50 flex items-center gap-1.5 shadow-sm">
              <Check className="w-3.5 h-3.5 stroke-[3]" />
              {collectedCount} đã sở hữu
            </span>
            <span className="px-3 py-1.5 rounded-xl bg-[#ff3366]/20 text-[#fecdd3] text-xs font-bold border border-[#ff3366]/40">
              {totalCount - collectedCount} tem đang chờ bạn
            </span>
            <span className="px-3 py-1.5 rounded-xl bg-gradient-to-r from-[#ffd166] to-[#f59e0b] text-[#331800] text-xs font-black shadow-md flex items-center gap-1">
              <Gift className="w-3.5 h-3.5 text-[#331800]" />
              Thưởng: +50 XP / Tem
            </span>
          </div>

          <div className="pt-3">
            <button
              onClick={onOpenScanner}
              className="gf-button text-sm py-3 px-6 shadow-xl flex items-center gap-2"
            >
              <QrCode className="w-4 h-4" />
              <span>📸 Bật Camera Quét QR</span>
            </button>
          </div>
        </div>
      </section>

      {/* COLLECTION SWITCHER TABS */}
      <div className="collection-switcher">
        <button type="button" aria-pressed="true">
          <strong>01 · Quốc bảo Việt Nam 👑</strong>
          <span>Đang mở · {collectedCount}/{totalCount} tem có thể săn</span>
        </button>
        <button type="button" disabled className="opacity-50 cursor-not-allowed">
          <strong>02 · Tranh sơn mài cổ truyền 🎨</strong>
          <span>Sắp mở mắt · Mùa 2</span>
        </button>
        <button type="button" disabled className="opacity-50 cursor-not-allowed">
          <strong>03 · Điêu khắc Phật giáo 🗿</strong>
          <span>Sắp mở mắt · Mùa 3</span>
        </button>
      </div>

      {/* CURRENT MISSION TARGET */}
      <section className="scan-mission">
        <div className="scan-mission__visual">
          <img
            src="/images/museum/web/interior-gallery-buddhist-sculpture.webp"
            alt="Phòng trưng bày hiện vật"
          />
          <span>🎯 Mục Tiêu Săn Tem 0{nextTarget.id + 1} / 0{totalCount}</span>
        </div>

        <div className="scan-mission__copy">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#ffb703]/20 border border-[#ffd166]/50 text-[#ffd166] text-[11px] font-black uppercase tracking-wider w-max mb-2">
            <Sparkles className="w-3 h-3" />
            <span>NHIỆM VỤ TIẾP THEO</span>
          </div>

          <h2 className="text-2xl sm:text-3xl font-black text-white leading-tight">
            Truy tìm “{nextTarget.title}” 🔍
          </h2>

          <p className="mt-2 text-[#e8d4c5] text-sm leading-relaxed">
            Đến khu vực <strong className="text-[#ffd166] underline decoration-wavy">{nextTarget.location}</strong>, đối chiếu bảng thông tin bảo vật rồi mở máy ảnh quét mã QR để rinh ngay tem số và <strong>+50 XP</strong>!
          </p>

          <ul className="scan-checks">
            <li>Tới đúng phòng trưng bày tại Bảo tàng (66 Nguyễn Thái Học)</li>
            <li>Mã QR riêng biệt dán tại chân hiện vật hoặc biển chú giải</li>
            <li>Mở khóa tức thì thẻ bài huyền thoại vào Balo của bạn</li>
          </ul>

          <div className="pt-2">
            <button
              onClick={onOpenScanner}
              className="gf-button text-xs py-3 px-5 flex items-center gap-2 w-max"
            >
              <QrCode className="w-4 h-4" />
              <span>Quét mã QR mở khóa tác phẩm này</span>
            </button>
          </div>
        </div>
      </section>

      {/* 9 STAMPS ALBUM: TRADING CARD BINDER */}
      <section className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b-2 border-[#ffb703]/30 pb-4">
          <div>
            <span className="text-xs font-black text-[#ffd166] uppercase tracking-widest flex items-center gap-1.5">
              <Trophy className="w-3.5 h-3.5 text-[#ffd166]" />
              SỔ TEM BẢO VẬT HOÀNG GIA
            </span>
            <h2 className="text-2xl sm:text-3xl font-black text-white mt-1">
              Bộ 09 Thẻ Bài Quốc Bảo Vô Giá
            </h2>
          </div>

          {/* Filter Pills */}
          <div className="flex items-center gap-1.5 bg-[#25132d] p-1.5 rounded-2xl border border-[#ffb703]/30 self-start sm:self-auto">
            <button
              type="button"
              onClick={() => setActiveFilter('all')}
              className={`px-3 py-1 rounded-xl text-xs font-black transition-all ${
                activeFilter === 'all'
                  ? 'bg-gradient-to-r from-[#ffd166] to-[#f59e0b] text-[#331800] shadow-sm'
                  : 'text-[#e0d0c5] hover:text-white'
              }`}
            >
              Tất cả ({totalCount})
            </button>
            <button
              type="button"
              onClick={() => setActiveFilter('unlocked')}
              className={`px-3 py-1 rounded-xl text-xs font-black transition-all ${
                activeFilter === 'unlocked'
                  ? 'bg-[#06d6a0] text-[#022c22] shadow-sm'
                  : 'text-[#e0d0c5] hover:text-white'
              }`}
            >
              Đã mở ({collectedCount})
            </button>
            <button
              type="button"
              onClick={() => setActiveFilter('locked')}
              className={`px-3 py-1 rounded-xl text-xs font-black transition-all ${
                activeFilter === 'locked'
                  ? 'bg-[#ff3366] text-white shadow-sm'
                  : 'text-[#e0d0c5] hover:text-white'
              }`}
            >
              Chưa mở ({totalCount - collectedCount})
            </button>
          </div>
        </div>

        {/* Card Grid */}
        <div className="album">
          {filteredArtifacts.map((art) => (
            <div
              key={art.id}
              onClick={() => onOpenArtifact(art)}
              className={`album-item group ${!art.unlocked ? 'locked' : ''}`}
            >
              <div className="thumb relative">
                <img
                  src={art.stamp}
                  alt={art.title}
                  className={`w-full h-full object-contain ${
                    !art.unlocked ? 'grayscale-[85%] opacity-75' : ''
                  }`}
                />
                {!art.unlocked && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center rounded-xl">
                    <Lock className="w-6 h-6 text-[#ffd166]/80 animate-pulse" />
                  </div>
                )}
              </div>

              <div className="album-copy">
                <div>
                  <div className="flex items-center justify-between">
                    <span className="index">0{art.id + 1} · QUỐC BẢO</span>
                    <span className="text-[10px] text-[#ffd166] font-bold">⭐⭐⭐⭐⭐</span>
                  </div>
                  <h3 className="line-clamp-1 font-black text-white group-hover:text-[#ffd166] transition-colors">
                    {art.title}
                  </h3>
                  <p className="line-clamp-1 text-xs text-[#d8c4b8]">
                    {art.author} · {art.material}
                  </p>
                </div>

                <div>
                  <span className="status">
                    {art.unlocked ? '✨ Đã thu thập (Xem 3D)' : '🔒 Chưa mở · Quét QR'}
                  </span>
                  <span className="view-detail block group-hover:underline">
                    Xem chi tiết thẻ →
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 4-STEPS GAMIFIED QR GUIDE */}
      <section className="relative rounded-3xl overflow-hidden border-2 border-[#ffb703]/40 bg-gradient-to-b from-[#281332] via-[#1d0d24] to-[#160a1a] p-6 sm:p-10 shadow-2xl space-y-8">
        {/* Ambient glow decoration */}
        <div
          className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#ffb703]/15 blur-3xl pointer-events-none"
          aria-hidden="true"
        />
        <div
          className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full bg-[#ff3366]/20 blur-3xl pointer-events-none"
          aria-hidden="true"
        />

        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto space-y-2 relative z-10">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#ffb703]/15 border border-[#ffd166]/50 text-[#ffd166] text-xs font-black uppercase tracking-widest">
            <Compass className="w-3.5 h-3.5 text-[#ffd166]" />
            <span>HƯỚNG DẪN ĐI TOUR THỰC ĐỊA CHO HỌC SINH</span>
          </div>
          <h2 className="text-2xl sm:text-4xl font-black text-white leading-tight">
            04 Bước Phá Đảo Chuyến Đi Bảo Tàng 🧭
          </h2>
          <p className="text-xs sm:text-sm text-[#e0d0c5] max-w-xl mx-auto leading-relaxed font-medium">
            Áp dụng trực tiếp tại các phòng trưng bày của Bảo tàng Mỹ thuật Việt Nam (66 Nguyễn Thái Học, Ba Đình, Hà Nội).
          </p>
        </div>

        {/* Responsive Steps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 relative z-10">
          {GUIDE_STEPS.map((s) => {
            const IconComp = s.icon;
            return (
              <div
                key={s.step}
                className="relative group rounded-2xl bg-[#2b1536]/80 hover:bg-[#381a47] border-2 border-white/10 hover:border-[#ffd166] p-5 flex flex-col justify-between transition-all duration-300 shadow-md hover:shadow-xl hover:-translate-y-1"
              >
                <div className="space-y-3">
                  {/* Step header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-7 h-7 rounded-xl bg-gradient-to-br from-[#ffd166] to-[#f59e0b] text-[#331800] font-mono font-black text-xs flex items-center justify-center shadow-md">
                        {s.step}
                      </span>
                      <span className="text-[10px] font-black uppercase tracking-wider text-[#ffd166]">
                        {s.phase}
                      </span>
                    </div>

                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center border shadow-inner transition-transform group-hover:scale-110 ${s.iconBg}`}>
                      <IconComp className={`w-4 h-4 ${s.iconColor}`} />
                    </div>
                  </div>

                  {/* Step Title */}
                  <h3 className="text-sm sm:text-base font-black text-white leading-snug">
                    {s.title}
                  </h3>

                  {/* Step Description */}
                  <p className="text-xs text-[#e8d4c5] leading-relaxed font-medium">
                    {s.desc}
                  </p>
                </div>

                {/* Step Footer */}
                <div className="pt-3.5 mt-2 border-t border-white/10 flex items-center justify-between flex-wrap gap-2">
                  <span className="text-[10.5px] font-bold px-2 py-0.5 rounded-full bg-black/40 border border-white/15 text-[#ffd166] flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#ffd166]" />
                    {s.badge}
                  </span>

                  {s.hasScannerCta && (
                    <button
                      onClick={onOpenScanner}
                      className="inline-flex items-center gap-1 text-[11px] font-black text-[#ff708f] hover:text-white transition-colors ml-auto group-hover:underline active:scale-95"
                    >
                      <span>Quét ngay</span>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer Reassurance & Action Strip */}
        <div className="rounded-2xl bg-black/45 border-2 border-[#ffd166]/40 p-4 sm:p-5 flex flex-col sm:flex-row items-center justify-between gap-4 relative z-10">
          <div className="flex items-start gap-3 w-full sm:w-auto">
            <div className="w-10 h-10 rounded-2xl bg-[#ffb703]/20 border border-[#ffd166]/50 flex items-center justify-center text-[#ffd166] shrink-0 mt-0.5 shadow-md">
              <Compass className="w-5 h-5" />
            </div>
            <div className="space-y-0.5 text-left">
              <h4 className="text-xs sm:text-sm font-black text-white">
                Bảo tàng mở cửa hàng ngày: 08:30 – 17:00 (Kể cả Thứ 7, Chủ Nhật)
              </h4>
              <p className="text-[11.5px] sm:text-xs text-[#e0d0c5] leading-relaxed font-medium">
                Vào cửa miễn phí hoặc giá ưu đãi cho học sinh có thẻ học sinh! Cho phép trình duyệt truy cập máy ảnh để quét mã QR nhé.
              </p>
            </div>
          </div>

          <button
            onClick={onOpenScanner}
            className="gf-button text-xs py-3 px-6 flex items-center gap-2 shrink-0 w-full sm:w-auto justify-center shadow-xl"
          >
            <QrCode className="w-4 h-4" />
            <span>📸 Mở camera quét QR</span>
          </button>
        </div>
      </section>
    </div>
  );
};
